import { useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentTrackCallback } from '@zenbusiness/application-commons-experiments';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { ActionButtonsGrid, FocusGrid, ContainerGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import SenderDetail from '#client/components/MoveMoney/components/SummaryFormComponents/SenderDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import {
  convertCentToUSDText,
  isChaseBank,
  resetCounterpartyOnUrl
} from '#client/components/MoveMoney/utils/common';
import { DATE_FORMAT } from '#client/constants/date';
import { COUNTERPARTY_ID } from '#client/constants/modals';
import { TOKEN_TYPES } from '#client/constants/tokens';
import {
  CONFIRMATION_FORM_STEP,
  HOW_MUCH_STEP,
  MOVE_MONEY_AGREEMENT,
  TIME_LINE,
  TRANSFER_TYPE,
  WHICH_ACCOUNT_STEP
} from '#client/constants/transfer';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useDepositContext from '#client/hooks/useDepositContext';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';

import { DialogContentStyled, Wrapper } from './IsThisCorrectFormv2.styles';

export default function IsThisCorrectFormV2({ handleClose }) {
  const trackExperiment = useExperimentTrackCallback();
  const { clearAllModal } = useModalContext();
  const { depositV2 } = useDepositContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const counterpartyIdFromUrl = searchParams.get(COUNTERPARTY_ID);

  const { data, setCurrentFormStep, setData } = useFormStep();

  const { selectedFromAccount, amount, toAccount, description, idempotencyKey } = data;
  const fromName = selectedFromAccount?.name ?? '';
  const fromBank = selectedFromAccount?.bank ?? '';
  const isHideEyeBtn = isChaseBank({
    bank: fromBank,
    connectedType: selectedFromAccount?.connectedType
  });
  const date = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);
  const amountInUSD = convertCentToUSDText(amount);
  const toName = toAccount?.name ?? '';

  useModalDisplayTracker({
    elementName: 'authorize_transaction_dm_modal',
    appPageName: 'active_banking_dashboard',
    elementText: 'authorize_transaction_dm_modal'
  });

  const handleEditTransactionDetails = () => {
    setCurrentFormStep(HOW_MUCH_STEP);
    setData({
      ...data,
      isDisableBack: false
    });
    if (counterpartyIdFromUrl) {
      resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
    }
  };

  const handleEditSender = () => {
    setCurrentFormStep(WHICH_ACCOUNT_STEP);
    setData({
      ...data,
      isDisableBack: false,
      transferType: TRANSFER_TYPE.STANDARD_ACH.value
    });
    if (counterpartyIdFromUrl) {
      resetCounterpartyOnUrl(setSearchParams, COUNTERPARTY_ID);
    }
  };

  const { getCustomerToken, loading: loadingToken } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });
  const authorizeButtonId = useEnterSubmit();

  const loadingSubmit = loadingToken;

  const trackAuthorizeClick = useButtonPressedCallback({
    elementName: 'authorize',
    elementText: 'Authorize',
    appPageName: 'authorize_transaction_dm_modal'
  });

  const handleSubmitDeposit = async () => {
    trackExperiment('Banking account funded');
    trackAuthorizeClick();

    const counterpartyId = data?.selectedFromAccount?.id;
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      handleClose();
      clearAllModal();
      return;
    }

    depositV2({
      counterpartyId,
      idempotencyKey,
      customerToken,
      amount,
      description: description || undefined
    });

    setCurrentFormStep(CONFIRMATION_FORM_STEP);
  };

  const handleSubmit = () => {
    setData({
      ...data,
      dateInit: date
    });
    handleSubmitDeposit();
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <Stack
                alignItems="center"
                paddingBottom={{ xs: '32px', lg: '40px' }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Is this correct?
                </Heading>
              </Stack>
              <Paper variant="outlined">
                <TransactionDetail
                  amount={amountInUSD}
                  dateInitiated={date}
                  handleEdit={handleEditTransactionDetails}
                  hasEdit
                  timeline={TIME_LINE.SAME_DAY_ACH}
                  title="Deposit details"
                  transactionDescription={description}
                  transferType="ACH"
                />
                <Divider />
                <RecipientDetail
                  accountHolder={toName}
                  accountNumber={toAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={toAccount?.accountNumber}
                />
                <Divider />
                <SenderDetail
                  accountHolder={fromName}
                  accountNumber={selectedFromAccount?.accountNumber}
                  bank={fromBank}
                  fullAccountNumber={isHideEyeBtn ? '' : selectedFromAccount?.fullAccountNumber}
                  handleEdit={handleEditSender}
                  hasEdit
                />
              </Paper>
              <Box marginTop={{ xs: '32px', lg: '40px' }}>
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_1}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_2}
                </Text>
                <br />
                <Text
                  align="left"
                  color="tertiary"
                  size="sm"
                >
                  {MOVE_MONEY_AGREEMENT.LINE_3}
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          disabled={loadingSubmit}
          fullWidth
          id={authorizeButtonId}
          loading={loadingSubmit}
          onClick={handleSubmit}
          size="large"
          variant="contained"
        >
          Authorize
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

IsThisCorrectFormV2.propTypes = {
  handleClose: PropTypes.func.isRequired
};
